import type { PayloadAction, SliceCaseReducers } from "@reduxjs/toolkit";
import type {
  AiTransform,
  OutputImage,
  OutputImageSideState,
  SpecsEditorSession,
  SpecsLayer,
  SpecsLayerRuntimeState,
  Transformation,
  TransformationType,
} from "../../../../containers/SpecsEditor/types";
import {
  DEFAULT_TRANSFORMATIONS,
  SpecsImageProcessingStatus,
} from "../../../../containers/SpecsEditor/types";
import { generateId, touch } from "../helpers";

export function defaultAiTransform(): AiTransform {
  return {
    prompt: "",
    method: "gpt-image-2",
    status: SpecsImageProcessingStatus.IDLE,
    generations: [],
    acceptedImageUrl: null,
  };
}

export function setAssetPreviewValue(
  target: Record<string, any> | null | undefined,
  key: string,
  previewValue?: string | null
) {
  if (!target || !previewValue) return;
  Object.defineProperty(
    target,
    `__preview${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    {
      value: previewValue,
      enumerable: false,
      configurable: true,
    }
  );
}

export function getOutputPreviewUrl(img: OutputImage | null | undefined) {
  if (!img) return null;
  const front = getOutputImageSide(img, "front");
  if (!front) return null;
  return (
    (front as Record<string, any>).__previewOutputImageUrl ??
    front.outputImageUrl ??
    null
  );
}

export function getOutputBackPreviewUrl(img: OutputImage | null | undefined) {
  if (!img) return null;
  const back = getOutputImageSide(img, "back");
  if (!back) return null;
  return (
    (back as Record<string, any>).__previewOutputImageUrl ??
    back.outputImageUrl ??
    null
  );
}

export function createDefaultTransformation(
  type: TransformationType,
  paramOverrides?: Record<string, number | string>
): Transformation {
  const defaultParams: Record<
    TransformationType,
    Record<string, number | string>
  > = {
    adjustContrast: { contrast: 0 },
    adjustSaturation: { saturation: 0 },
    adjustBrightness: { brightness: 0 },
    sharpen: { radius: 20, strength: 2 },
    invertColors: {},
    autoMask: {},
    alphaToMask: {},
    whiteToMask: {},
    blackToMask: {},
  };

  return {
    id: generateId(),
    type,
    enabled: true,
    params: { ...defaultParams[type], ...paramOverrides },
    status: SpecsImageProcessingStatus.IDLE,
  };
}

function getDefaultTransformationsForRole(
  role: "highlight" | "mask" | "none"
): Transformation[] {
  const base = DEFAULT_TRANSFORMATIONS.map((type) =>
    createDefaultTransformation(type)
  );
  if (role === "highlight") {
    base.push(
      createDefaultTransformation("adjustSaturation", { saturation: -100 })
    );
  }
  return base;
}

export function createDefaultOutputImageSide(
  role: "highlight" | "mask" | "none" = "none"
): OutputImageSideState {
  return {
    sourceLayerId: null,
    referenceImageIds: [],
    transformations: getDefaultTransformationsForRole(role),
    aiTransform: defaultAiTransform(),
    outputImageUrl: null,
  };
}

export function createDefaultOutputImage(
  name: string,
  role: "highlight" | "mask" | "none" = "none",
  sideNames: string[] = ["front"]
): OutputImage {
  const normalizedSideNames = sideNames.length > 0 ? sideNames : ["front"];
  const sides = Object.fromEntries(
    normalizedSideNames.map((sideName) => [
      sideName,
      createDefaultOutputImageSide(role),
    ])
  );
  const front = sides.front ?? sides[normalizedSideNames[0]];

  return {
    id: generateId(),
    name,
    role,
    backgroundColor: "#ffffff",
    ...front,
    sides,
    back: sides.back ?? null,
  };
}

/** @deprecated Use createDefaultOutputImageSide. */
export function createDefaultBack(
  role: "highlight" | "mask" | "none" = "none"
) {
  return createDefaultOutputImageSide(role);
}

export function getOutputImageSide(
  img: OutputImage,
  side: string = "front"
): OutputImageSideState | null {
  if (img.sides?.[side]) {
    return img.sides[side];
  }
  if (side === "front") {
    return img;
  }
  if (side === "back") {
    return img.back ?? null;
  }
  return null;
}

export function setOutputImageSide(
  img: OutputImage,
  side: string,
  nextSide: OutputImageSideState | null
) {
  if (!img.sides) {
    img.sides = {};
  }

  if (nextSide) {
    img.sides[side] = nextSide;
  } else {
    delete img.sides[side];
  }

  if (side === "front" && nextSide) {
    img.sourceLayerId = nextSide.sourceLayerId;
    img.referenceImageIds = nextSide.referenceImageIds;
    img.transformations = nextSide.transformations;
    img.aiTransform = nextSide.aiTransform;
    img.outputImageUrl = nextSide.outputImageUrl;
  }
  if (side === "back") {
    img.back = nextSide;
  }
}

export function getOutputImageSideNames(img: OutputImage): string[] {
  const sideNames = Object.keys(img.sides ?? {});
  const names = sideNames.length > 0 ? sideNames : ["front"];
  if (img.back && !names.includes("back")) {
    names.push("back");
  }
  return names;
}

export function layerImages(layer: SpecsLayer): OutputImage[] {
  return [layer.highlight, layer.mask].filter((image): image is OutputImage =>
    Boolean(image)
  );
}

export function allImages(state: SpecsEditorSession): OutputImage[] {
  return Object.values(state.layerRuntimeByKey).flatMap((layer) =>
    layer.highlight ? [layer.highlight, layer.mask] : [layer.mask]
  );
}

export function findImage(
  state: SpecsEditorSession,
  imageId: string
): [SpecsLayerRuntimeState | null, OutputImage | null] {
  for (const layer of Object.values(state.layerRuntimeByKey)) {
    const images = layer.highlight
      ? [layer.highlight, layer.mask]
      : [layer.mask];
    for (const img of images) {
      if (img.id === imageId) return [layer, img];
    }
  }
  return [null, null];
}

export function resolveSide(
  img: OutputImage,
  side?: string
): OutputImageSideState | null {
  return getOutputImageSide(img, side ?? "front");
}

export const imageReducers = {
  setProcessedImageUrl(
    state,
    {
      payload,
    }: PayloadAction<{
      imageId: string;
      url: string;
      side?: string;
    }>
  ) {
    for (const layer of Object.values(state.layerRuntimeByKey)) {
      const images = layer.highlight
        ? [layer.highlight, layer.mask]
        : [layer.mask];
      for (const img of images) {
        if (img.id !== payload.imageId) continue;
        const target = resolveSide(img, payload.side);
        if (target) {
          target.outputImageUrl = payload.url;
          setOutputImageSide(img, payload.side ?? "front", target);
        }
        return;
      }
    }
  },

  setAutoMaskFilter(
    state,
    {
      payload,
    }: PayloadAction<{
      imageId: string;
      filterType: "alphaToMask" | "whiteToMask" | "blackToMask";
      side?: string;
    }>
  ) {
    const [, img] = findImage(state, payload.imageId);
    if (!img) return;
    const target = resolveSide(img, payload.side);
    if (!target) return;
    const maskFilters = ["alphaToMask", "whiteToMask", "blackToMask"];
    target.transformations = target.transformations.filter(
      (t) => !maskFilters.includes(t.type)
    );
    target.transformations.unshift(
      createDefaultTransformation(payload.filterType)
    );
    touch(state);
  },

  setReferenceImageUrl(state, { payload }: PayloadAction<string | null>) {
    state.referenceImageUrl = payload;
  },
} satisfies SliceCaseReducers<SpecsEditorSession>;
